import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminMessages, agentTickets } from '../services/api';
import {
  Users, ShoppingCart, Package, BarChart2,
  Settings, Menu, LogOut, Wine, Star, ExternalLink,
  Home, Calendar, BookOpen, Inbox, Activity,
} from 'lucide-react';

const NAV = [
  { to: '/admin/dashboard', icon: Home,         label: 'Dashboard' },
  { to: '/admin/customers', icon: Users,        label: 'Customers' },
  { to: '/admin/orders',    icon: ShoppingCart, label: 'Orders' },
  {
    to: '/admin/products', icon: Package, label: 'Products',
    subLinks: [
      { to: '/admin/products/featured', icon: Star, label: 'Featured' },
    ],
  },
  { to: '/admin/reports',          icon: BarChart2,    label: 'Reports' },
  { to: '/admin/crm/follow-ups',   icon: Calendar,     label: 'Follow-Ups',   groupLabel: 'CRM' },
  { to: '/admin/agent/queue',      icon: Inbox,        label: 'Agent Queue',  groupLabel: 'Agent', agentBadge: true },
  { to: '/admin/qbo',              icon: BookOpen,     label: 'QuickBooks',   groupLabel: 'Integrations' },
  { to: '/wholesale/home',         icon: ExternalLink, label: 'Preview Store', newTab: true },
];

const SIDEBAR_WIDTH = 240;

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unreadCount,       setUnreadCount]       = useState(0);
  const [pendingTicketCount, setPendingTicketCount] = useState(0);

  useEffect(() => {
    const fetchCount = () => {
      adminMessages.getUnreadCount()
        .then((res) => setUnreadCount(res.data.count || 0))
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 120000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!['admin', 'sales_rep', 'customer_service'].includes(user?.role)) return;
    const fetchPending = () => {
      agentTickets.list({ status: 'pending', limit: 1 })
        .then((res) => setPendingTicketCount(res.data.data?.pagination?.total || 0))
        .catch(() => {});
    };
    fetchPending();
    const interval = setInterval(fetchPending, 120000);
    return () => clearInterval(interval);
  }, [user?.role]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const navLinkClass = ({ isActive }) =>
    `nav-link d-flex align-items-center gap-2 px-3 py-2 rounded mb-1 ${
      isActive ? 'bg-primary text-white' : 'text-white-50'
    }`;

  const sidebar = (
    <div className="d-flex flex-column h-100 bg-dark text-white py-3 px-2">
      {/* Brand */}
      <div className="d-flex align-items-center gap-2 px-3 mb-4">
        <Wine size={22} />
        <span className="fw-semibold" style={{ fontSize: 15 }}>JWC Admin</span>
      </div>

      {/* Nav links */}
      <nav className="flex-grow-1">
        {NAV.map(({ to, icon: Icon, label, subLinks, newTab, groupLabel, agentBadge }) => (
          <React.Fragment key={to}>
            {groupLabel && (
              <div
                className="px-3 mt-3 mb-1"
                style={{ fontSize: '0.65rem', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', fontWeight: 600 }}
              >
                {groupLabel}
              </div>
            )}
            {newTab ? (
              <a
                href={to}
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link d-flex align-items-center gap-2 px-3 py-2 rounded mb-1 text-white-50"
              >
                <Icon size={16} />
                {label}
                <ExternalLink size={11} className="ms-auto opacity-50" />
              </a>
            ) : (
              <NavLink to={to} end className={navLinkClass}>
                <Icon size={16} />
                {label}
                {to === '/admin/customers' && unreadCount > 0 && (
                  <span className="badge bg-warning text-dark ms-auto" style={{ fontSize: 10 }}>
                    {unreadCount}
                  </span>
                )}
                {agentBadge && pendingTicketCount > 0 && (
                  <span className="badge bg-danger ms-auto" style={{ fontSize: 10 }}>
                    {pendingTicketCount}
                  </span>
                )}
              </NavLink>
            )}
            {subLinks?.map(({ to: subTo, icon: SubIcon, label: subLabel }) => (
              <NavLink
                key={subTo}
                to={subTo}
                className={navLinkClass}
                style={{ paddingLeft: '2rem', fontSize: 13 }}
              >
                <SubIcon size={13} />
                {subLabel}
              </NavLink>
            ))}
          </React.Fragment>
        ))}
        {/* Agent monitoring — admin only */}
        {user?.role === 'admin' && (
          <NavLink to="/admin/agent/monitoring" className={navLinkClass} style={{ paddingLeft: '2rem', fontSize: 13 }}>
            <Activity size={13} />
            Monitoring
          </NavLink>
        )}
        {user?.role === 'admin' && (
          <NavLink to="/admin/settings" className={navLinkClass}>
            <Settings size={16} />
            Settings
          </NavLink>
        )}
      </nav>

      {/* User footer */}
      <div className="border-top border-secondary pt-3 px-3">
        <p className="small text-white-50 mb-1">{user?.firstName} {user?.lastName}</p>
        <button
          className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 w-100"
          onClick={handleLogout}
        >
          <LogOut size={13} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="d-flex" style={{ minHeight: '100vh' }}>

      {/* ── Desktop sidebar ── */}
      <div
        className="d-none d-lg-flex flex-column flex-shrink-0"
        style={{ width: SIDEBAR_WIDTH, position: 'sticky', top: 0, height: '100vh' }}
      >
        {sidebar}
      </div>

      {/* ── Mobile offcanvas sidebar ── */}
      <div
        className="offcanvas offcanvas-start bg-dark"
        tabIndex="-1"
        id="adminSidebar"
        style={{ width: SIDEBAR_WIDTH }}
      >
        <div className="offcanvas-header border-bottom border-secondary">
          <h6 className="text-white mb-0">JWC Admin</h6>
          <button
            type="button"
            className="btn-close btn-close-white"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          />
        </div>
        <div className="offcanvas-body p-0">{sidebar}</div>
      </div>

      {/* ── Main column ── */}
      <div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>

        {/* Top bar */}
        <nav className="navbar navbar-dark bg-dark px-3 sticky-top border-bottom border-secondary">
          {/* Mobile hamburger */}
          <button
            className="navbar-toggler d-lg-none border-0 p-1"
            type="button"
            data-bs-toggle="offcanvas"
            data-bs-target="#adminSidebar"
            aria-controls="adminSidebar"
          >
            <Menu size={20} className="text-white" />
          </button>

          <span className="navbar-brand mb-0 h6 d-none d-lg-block">
            Jeremy Wine Co. — Admin
          </span>
          <span className="navbar-brand mb-0 h6 d-lg-none">JWC Admin</span>

          <span className="text-white-50 small ms-auto">
            {user?.firstName} {user?.lastName}
          </span>
        </nav>

        {/* Page content */}
        <main className="flex-grow-1 p-4 bg-light">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
